<script setup>
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { useReservationsStore } from '@/store/reservation-store';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
    contractType: {
        type: String,
        default: 'SHORT'
    },
    reservationNo: {
        type: String,
        defalt: ''
    },
    validMsg: {
        type: Object,
        default: () => ({})
    },
    requiredList: {
        type: [Array, Object],
        default: () => []
    },
    readonlyList: {
        type: [Array, Object],
        default: () => []
    },
    hideList: {
        type: [Array, Object],
        default: () => []
    },
    reservationData: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update']);
const route = useRoute();
const store = useReservationsStore();
const sessionKey = `${route.name}`;
const toastComponentRef = ref(null);
const showSchedulePreviewPopup = ref(false);
const showSchedulePopup = ref(false);
let referralList = ref([]);
const displayTotalAmount = ref('');
const rentalPreviewText = ref('');
const previewData = ref(null);
const priceUIState = ref('idle');
const totalAmountLocked = ref(false);
const hasExternalTotalAmount = ref(false);
const paymentScheduleList = ref([]);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
// const codes = computed(() => useCommonCodeStore().getCommonCodes)
// const getCommonCode = (code) => codes.value?.[code] ?? []
const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};

const generateArray = (length, unit) => {
    return Array.from({ length }, (_, i) => ({
        id: i + 1,
        value: i + 1,
        text: `${i + 1} ${unit}`
    }));
};

const initialData = {
    month_amount: '',
    contract_month: '',
    payment_day: '',
    payment_first_date: '',
    deposit_payment_amount: '',
    advance_payment: '',
    total_amount: '',
    referral_id: ''
};

const data = reactive({ ...initialData });
const sessionRef = store.ensure(sessionKey);

const commonSyncKeys = computed(() => Object.keys(data).filter((k) => Object.prototype.hasOwnProperty.call(sessionRef, k)));
const pickKeys = (src, keys) =>
    keys.reduce((o, k) => {
        if (k in src) o[k] = src[k];
        return o;
    }, {});

let pushing = false;
let pulling = false;

const requiredData = [];
const requiredKeys = computed(() => {
    const res = props.requiredList;
    if (Array.isArray(res) && res.length) return res;
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return Object.keys(res).filter((key) => !!res[key]);
    }
    return requiredData;
});

const requiredKeySet = computed(() => new Set(requiredKeys.value));
const isRequired = (key) => requiredKeySet.value.has(key);

const initialValidMessage = {
    month_amount: '',
    contract_month: '',
    payment_day: '',
    payment_first_date: '',
    deposit_payment_amount: '',
    advance_payment: '',
    total_amount: '',
    referral_id: ''
};
const validationMessage = reactive({ ...initialValidMessage });

const allReadonly = ref(false);
const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};
const allowedKeys = Object.keys(initialData);

const propsReadonlyEnforced = computed(() => {
    const res = props.readonlyList;
    const set = new Set();
    if (Array.isArray(res)) res.forEach((k) => set.add(k));
    else if (res && typeof res === 'object') {
        Object.keys(res).forEach((k) => {
            if (res[k]) set.add(k);
        });
    }
    return set;
});

const finalReadonlySet = computed(() => {
    const enforced = propsReadonlyEnforced.value;
    const result = new Set(enforced);

    if (allReadonly.value) {
        allowedKeys.forEach((k) => {
            if (!enforced.has(k)) result.add(k);
        });
    }
    return result;
});

const isFieldReadonly = (key) => finalReadonlySet.value.has(key);
const isEffectivelyRequired = (key) => requiredKeySet.value.has(key) && !isFieldReadonly(key) && !isHidden(key);

const isShort = computed(() => props.contractType === 'SHORT');
const isInsu = computed(() => props.contractType === 'INSU');
const isMidOrLong = computed(() => props.contractType === 'MID' || props.contractType === 'LONG');

const typeVisibleMap = {
    SHORT: new Set(['total_amount', 'referral_id']),
    INSU: new Set(['referral_id']),
    DEFAULT: new Set(['month_amount', 'contract_month', 'payment_day', 'payment_first_date', 'deposit_payment_amount', 'advance_payment', 'total_amount', 'referral_id'])
};
const typeVisibleKeySet = computed(() => typeVisibleMap[props.contractType] ?? typeVisibleMap.DEFAULT);

const propsHideKeySet = computed(() => {
    let res = props.hideList;
    if (typeof res === 'string') {
        try {
            res = JSON.parse(res);
        } catch {}
    }
    if (Array.isArray(res) && res.length) return new Set(res);
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return new Set(Object.keys(res).filter((key) => !!res[key]));
    }
    return new Set();
});

const hideKeySet = computed(() => {
    const keySet = new Set(propsHideKeySet.value);
    allowedKeys.forEach((key) => {
        if (!typeVisibleKeySet.value.has(key)) keySet.add(key);
    });
    return keySet;
});

const isHidden = (key) => hideKeySet.value.has(key);
const isTotalHidden = computed(() => isHidden('total_amount'));

const resetData = () => {
    Object.assign(data, initialData);
    Object.assign(validationMessage, initialValidMessage);
    hasExternalTotalAmount.value = false;
    store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
};
const excludeReturn = new Set(['vehicle_model_name', 'fuel_type']);
const getData = () => {
    const payload = {};
    for (const key of allowedKeys) {
        if (excludeReturn.has(key)) continue;
        if (!isHidden(key)) payload[key] = data[key];
    }
    return payload;
};

const setReservationData = (newVal) => {
    if (!newVal) return;
    const payload = newVal.contract_data ?? newVal;

    nextTick(() => {
        Object.entries(payload).forEach(([key, val]) => {
            if (allowedKeys.includes(key)) {
                data[key] = val;
            }
        });

        // reservationData에서 total_amount가 전달된 경우
        if (payload.total_amount) {
            hasExternalTotalAmount.value = true;
            const baseAmount = Number(payload.total_amount) || 0;
            const advanceAmount = Number(data.advance_payment || 0);

            // total_amount + advance_payment로 표시
            displayTotalAmount.value = baseAmount + advanceAmount;
            data.total_amount = displayTotalAmount.value;
        }
    });
};

const checkRequired = () => {
    Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    let result = true;

    const keysToCheck = new Set(requiredKeys.value.filter((key) => allowedKeys.includes(key) && isEffectivelyRequired(key)));

    for (const key of keysToCheck) {
        const val = data[key];
        const isEmpty = val === '' || val === null || val === undefined;
        const isIdInvalid = key.endsWith('_id') && (isEmpty || Number(val) === 0);
        if (isEmpty || isIdInvalid) {
            if (key in validationMessage) validationMessage[key] = '필수입력입니다.';
            result = false;
        }
    }
    return result;
};

const setErrorMessage = (payload, reset = false) => {
    if (!payload) return;

    if (reset) {
        Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    }

    const collectPairs = (node, out = []) => {
        if (node == null) return out;

        if (Array.isArray(node)) {
            node.forEach((item) => collectPairs(item, out));
            return out;
        }

        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    collectPairs(v, out);
                } else {
                    out.push([k, v]);
                }
            }
            return out;
        }

        return out;
    };

    const pairs = collectPairs(payload);

    for (const [rawKey, rawVal] of pairs) {
        let key = String(rawKey).replace(/^contract_data\./, '');
        if (!(key in validationMessage)) continue;

        let msg = rawVal;
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ');
        if (msg == null) msg = '';

        validationMessage[key] = String(msg);
    }
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
        return '0';
    }
    return Number(value).toLocaleString();
};

function resetPriceUI() {
    priceUIState.value = 'idle';
    totalAmountLocked.value = false;
}

function applyPreviewAmount() {
    if (allReadonly.value) return;
    if (!previewData.value) return;
    const amt = previewData.value.total_amount ?? '';
    displayTotalAmount.value = amt;
    data.total_amount = amt;
    totalAmountLocked.value = true;
    priceUIState.value = 'applied';
}

function enableAmountEdit() {
    if (allReadonly.value) return;
    totalAmountLocked.value = false;
    rentalPreviewText.value = '';
    previewData.value = null;
    resetPriceUI();
}

const getRentalPreview = async () => {
    if (allReadonly.value) return;

    const config = {
        method: 'post'
    };

    const payload = {
        vehicle_group_id: Number(sessionRef.vehicle_group_id) || 0,
        start_date: sessionRef.pickup_date || '',
        start_time: sessionRef.pickup_time || '',
        end_date: sessionRef.return_expected_date || '',
        end_time: sessionRef.return_expected_time || '',
        collision_insurance_state: sessionRef.collision_insurance_state || 'NON',
        insurance_age: sessionRef.collision_insurance_age ?? '',
        pickup_delivery_id: Number(sessionRef.pickup_delivery_id) || 0,
        return_expected_delivery_id: Number(sessionRef.return_expected_delivery_id) || 0
    };

    try {
        const { execute, setHeader } = useAxios('/api/contract/normal/preview', config, {
            immediate: false,
            onSuccess(response) {
                const result = response.data.data?.[0];
                //console.log('견적조회 결과', result.total_amount);
                previewData.value = result;
                rentalPreviewText.value = result.text ?? '';
                if (rentalPreviewText.value) priceUIState.value = 'previewed';
            },
            onError(err) {
                console.error('[ERROR]: ', err.errorMessage);
                toastComponentRef.value?.showToast('info', '알림', err.errorMessage);
                return;

                resetPriceUI();
            }
        });
        // setHeader('Content-Type', 'application/json');
        await execute(payload);
    } catch (err) {
        console.log(err);
        resetPriceUI();
    }
};

const searchReferralList = async () => {
    const config = { method: 'get' };
    try {
        const { execute } = useAxios('/api/referral/normal', config, {
            immediate: false,
            onSuccess(response) {
                // console.log(response.data.data);

                const newList = response.data.data.map((item, index) => ({
                    value: item.referral_id,
                    text: item.referral_name,
                    id: item.referral_id,
                    title: item.referral_name
                }));
                referralList.value = newList;
            },
            onError(err) {
                console.error('Error:', err.message);
                referralList.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        referralList.value = [];
    }
};

const numberFormatter = (n) => {
    const v = Number(n ?? 0);
    return isFinite(v) ? v.toLocaleString() : '0';
};

const parseLocalYMD = (v) => {
    if (v == null) return null;

    if (v instanceof Date) {
        const d = new Date(v);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    if (typeof v === 'number') v = String(v);
    if (typeof v === 'string') {
        const s = v.trim();

        let m = s.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/);
        if (!m) m = s.match(/^(\d{4})(\d{2})(\d{2})$/);

        if (m) {
            const y = +m[1],
                mo = +m[2] - 1,
                d = +m[3];
            const dt = new Date(y, mo, d);
            if (dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === d) {
                dt.setHours(0, 0, 0, 0);
                return dt;
            }
            return null;
        }
    }

    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return null;
    dt.setHours(0, 0, 0, 0);
    return dt;
};

const lastDayOfMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const clampDay = (y, m, day) => Math.min(day, lastDayOfMonth(y, m));
const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};
const paymentTotal = computed(() => {
    const months = Number(data.contract_month);
    const monthly = Number(data.month_amount);
    if (!months || !monthly) return 0;
    return months * monthly;
});

const grandTotal = computed(() => {
    const advance = Number(data.advance_payment || 0);
    return paymentTotal.value + advance;
});

const scheduleList = computed(() => {
    const months = Number(data.contract_month);
    const monthlyAmount = Number(data.month_amount);
    const paymentDay = Number(data.payment_day);
    const startDate = parseLocalYMD(sessionRef.pickup_date);

    if (!months || !monthlyAmount || !paymentDay || !startDate) return [];

    let firstDate;

    if (data.payment_first_date) {
        firstDate = parseLocalYMD(data.payment_first_date);
        // if (!firstDate || firstDate < startDate) return [];
    } else {
        const y = startDate.getFullYear();
        const m = startDate.getMonth();
        const ldom = lastDayOfMonth(y, m);
        const pdThisMonth = Math.min(paymentDay, ldom);
        const startDay = startDate.getDate();

        if (startDay === pdThisMonth) {
            firstDate = startDate;
        } else if (startDay < pdThisMonth) {
            firstDate = new Date(y, m, ldom);
        } else {
            const ny = m === 11 ? y + 1 : y;
            const nm = (m + 1) % 12;
            firstDate = new Date(ny, nm, clampDay(ny, nm, paymentDay));
        }
    }

    const result = [];
    for (let i = 0; i < months; i++) {
        if (i === 0) {
            result.push({
                installment: 1,
                paymentDate: formatDate(firstDate),
                paymentAmount: monthlyAmount
            });
        } else {
            const fy = firstDate.getFullYear();
            const fm = firstDate.getMonth() + i;
            const ty = fy + Math.floor(fm / 12);
            const tm = fm % 12;
            const td = clampDay(ty, tm, paymentDay);
            const payDate = new Date(ty, tm, td);
            result.push({
                installment: i + 1,
                paymentDate: formatDate(payDate),
                paymentAmount: monthlyAmount
            });
        }
    }
    return result;
});

function calculateFirstPaymentDate(startDate, paymentDay, manualFirstDateStr) {
    if (!startDate || !paymentDay) {
        return null;
    }

    if (manualFirstDateStr) {
        const firstDate = parseLocalYMD(manualFirstDateStr);

        if (firstDate && firstDate >= startDate) {
            return firstDate;
        }
        return null;
    }

    const y = startDate.getFullYear();
    const m = startDate.getMonth(); // 0-11
    const startDay = startDate.getDate();

    const ldom = lastDayOfMonth(y, m);
    const pdThisMonth = Math.min(paymentDay, ldom);

    if (startDay === pdThisMonth) {
        return startDate;
    } else if (startDay < pdThisMonth) {
        return new Date(y, m, ldom);
    } else {
        const ny = m === 11 ? y + 1 : y;
        const nm = (m + 1) % 12;
        return new Date(ny, nm, clampDay(ny, nm, paymentDay));
    }
}

const handleSchedulePreview = () => {
    if (!data.contract_month || data.contract_month === '0' || !data.month_amount || !data.payment_day) {
        toastComponentRef.value?.showToast('info', '알림', '계약개월, 월 납부금, 납부일은 필수 입력 항목입니다.');
        return;
    }
    if (!sessionRef.pickup_date) {
        toastComponentRef.value?.showToast('info', '알림', '배차(계약) 시작일이 필요합니다.');
        return;
    }
    // if (data.payment_first_date) {
    //     const first = parseLocalYMD(data.payment_first_date);
    //     const start = parseLocalYMD(sessionRef.pickup_date);
    //     if (first < start) {
    //         toastComponentRef.value?.showToast('info', '알림', '최초납부일은 배차시작일 이후여야 합니다.');
    //         return;
    //     }
    // }

    if (scheduleList.value.length === 0) {
        toastComponentRef.value?.showToast('info', '알림', '스케줄 정보를 계산할 수 없습니다.');
        return;
    }
    showSchedulePreviewPopup.value = true;
};

const handleClickSchedule = () => {
    const url = `/api/payment-schedule/${props.reservationNo}`;
    const config = {
        method: 'get'
    };

    const { data, error, execute } = useAxios(url, config, {
        immediate: false,
        onSuccess(response) {
            console.log('납부스케줄 조회', response.data.data);
            paymentScheduleList.value = response.data.data;
            console.log('paymentScheduleList', paymentScheduleList.value);
            showSchedulePopup.value = !showSchedulePopup.value;
        },
        onError(err) {
            if (toastComponentRef.value) {
                toastComponentRef.value.showToast('error', '조회 실패', err.message);
            }
            console.error('Error:', err.message);
        }
    });
    execute();
};

const closeSchedulePopup = () => {
    showSchedulePreviewPopup.value = false;
    showSchedulePopup.value = false;
};

onMounted(() => {
    searchReferralList();
    displayTotalAmount.value = data.total_amount ?? '';
});

watch(isMidOrLong, (newVal) => {
    if (newVal) {
        resetPriceUI();
    }
});

watch(
    () => props.reservationData,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') return;
        setReservationData(newVal);
    },
    { immediate: true, deep: false }
);

watch(
    () => props.readonlyList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isFieldReadonly(key)) validationMessage[key] = '';
        });
        // checkRequired();
    },
    { immediate: false, deep: false }
);

watch(
    () => props.hideList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isHidden(key)) validationMessage[key] = '';
        });
    },
    { immediate: false, deep: false }
);

watch(
    () => props.validMsg,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') {
            console.error('Invalid Error data');
            return;
        }
        setErrorMessage(newVal, true);
        // Object.keys(newVal).forEach((key) => {
        //     if (key.startsWith('contract_data.')) {
        //         const pureKey = key.replace('contract_data.', '');
        //         if (pureKey in validationMessage) {
        //             validationMessage[pureKey] = newVal[key];
        //         }
        //     } else {
        //         if (key in validationMessage) {
        //             validationMessage[key] = newVal[key];
        //         }
        //     }
        // });
    },
    { immediate: true }
);

watch(
    () => data.total_amount,
    (newVal) => {
        if (newVal !== displayTotalAmount.value) displayTotalAmount.value = newVal ?? '';
    },
    { immediate: true }
);

watch(displayTotalAmount, (newVal) => {
    if (data.total_amount !== newVal) data.total_amount = newVal ?? '';
});

watch(
    [paymentTotal, () => data.advance_payment, isMidOrLong],
    () => {
        if (isMidOrLong.value && !hasExternalTotalAmount.value) {
            displayTotalAmount.value = grandTotal.value || 0;
            data.total_amount = displayTotalAmount.value;
        }
    },
    { immediate: true }
);

watch(
    () => data.advance_payment,
    (newAdvancePayment) => {
        if (hasExternalTotalAmount.value && props.reservationData) {
            const payload = props.reservationData.contract_data ?? props.reservationData;
            if (payload.total_amount) {
                const baseAmount = Number(payload.total_amount) || 0;
                const advanceAmount = Number(newAdvancePayment || 0);
                displayTotalAmount.value = baseAmount + advanceAmount;
                data.total_amount = displayTotalAmount.value;
            }
        }
    }
);

watch(
    [() => data.contract_month, () => data.month_amount, () => data.payment_day, () => sessionRef.pickup_date],
    () => {
        if (data.payment_first_date) return;

        const months = Number(data.contract_month);
        const monthlyAmount = Number(data.month_amount);
        const paymentDay = Number(data.payment_day);
        const startDate = parseLocalYMD(sessionRef.pickup_date);

        if (!months || !monthlyAmount || !paymentDay || !startDate) return;

        const firstDate = calculateFirstPaymentDate(startDate, paymentDay);
        data.payment_first_date = formatDate(firstDate);
    },
    { immediate: true }
);

watch(
    () => commonSyncKeys.value.map((k) => data[k]),
    () => {
        if (pulling) return;
        pushing = true;
        store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
        pushing = false;
    },
    { deep: false, immediate: true }
);

watch(
    () => commonSyncKeys.value.map((k) => sessionRef[k]),
    () => {
        if (pushing) return;
        pulling = true;
        commonSyncKeys.value.forEach((k) => {
            if (sessionRef[k] !== undefined && data[k] !== sessionRef[k]) {
                data[k] = sessionRef[k];
            }
        });
        pulling = false;
    },
    { deep: false, immediate: true }
);

defineExpose({
    getData,
    resetData,
    setReservationData,
    checkRequired,
    setAllReadonly,
    setErrorMessage
});
</script>

<template>
    <ToastComponent ref="toastComponentRef" />
    <div class="section-box base-text">
        <div class="section-body">
            <div class="section-left">
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <h2 class="section-title z-[-10] py-4">예약 정보</h2>
                    </div>
                </div>
                <template v-if="isShort">
                    <div v-if="!isTotalHidden" class="section-row">
                        <div class="input-box w-[70%]">
                            <inputbox
                                inputId="total_amount"
                                v-model="displayTotalAmount"
                                :number="'n'"
                                :unit="'원'"
                                :errmsg="validationMessage.total_amount"
                                label="총 요금"
                                :placeholder="'총 요금'"
                                :maxlength="15"
                                :readonly="isFieldReadonly('total_amount') || totalAmountLocked"
                                :required="isEffectivelyRequired('total_amount')"
                            />
                        </div>
                        <div class="input-box w-[30%] flex items-center">
                            <template v-if="priceUIState === 'idle'">
                                <button
                                    type="button"
                                    @click="getRentalPreview"
                                    :class="[
                                        'h-[2.5rem] mt-6 w-full border font-semibold text-[0.9rem] rounded-[0.4rem]',
                                        allReadonly ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150'
                                    ]"
                                >
                                    요금 미리보기
                                </button>
                            </template>

                            <template v-else-if="priceUIState === 'previewed'">
                                <div class="flex gap-2 w-full mt-6">
                                    <button
                                        type="button"
                                        @click="applyPreviewAmount"
                                        :class="[
                                            'flex-1 h-[2.5rem] w-full border font-semibold text-[0.9rem] rounded-[0.4rem]',
                                            allReadonly ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150'
                                        ]"
                                    >
                                        요금 적용
                                    </button>
                                    <button
                                        type="button"
                                        @click="getRentalPreview"
                                        :class="[
                                            'flex-1 h-[2.5rem] w-full border font-semibold text-[0.9rem] rounded-[0.4rem]',
                                            allReadonly ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150'
                                        ]"
                                    >
                                        요금 계산
                                    </button>
                                </div>
                            </template>

                            <template v-else>
                                <div class="flex gap-2 w-full mt-6">
                                    <button
                                        type="button"
                                        @click="enableAmountEdit"
                                        :class="[
                                            'flex-1 h-[2.5rem] w-full border font-semibold text-[0.9rem] rounded-[0.4rem]',
                                            allReadonly ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150'
                                        ]"
                                    >
                                        요금 수정
                                    </button>
                                    <button
                                        type="button"
                                        @click="getRentalPreview"
                                        :class="[
                                            'flex-1 h-[2.5rem] w-full border font-semibold text-[0.9rem] rounded-[0.4rem]',
                                            allReadonly ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150'
                                        ]"
                                    >
                                        요금 계산
                                    </button>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="section-row">
                        <div v-if="!isTotalHidden" class="input-box w-[70%] relative">
                            <customtextbox
                                inputId="amount"
                                v-model="rentalPreviewText"
                                :height="'10rem'"
                                :errmsg="validationMessage.bodily_injury_excess"
                                label="요금 상세"
                                :readonly="true"
                                :placeholder="'필수입력 항목(차량정보, 배차 및 반납일시, 자차보험정보) 입력 후 요금 미리보기 표시'"
                            />
                        </div>
                        <div v-if="!isHidden('referral_id')" :class="['input-box', isTotalHidden ? 'w-[100%]' : 'w-[30%]']">
                            <combobox
                                inputId="referral_id"
                                v-model="data.referral_id"
                                :options="referralList"
                                :errmsg="validationMessage.referral_id"
                                label="소개처"
                                :placeholder="'소개처를 선택하세요.'"
                                :readonly="isFieldReadonly('referral_id')"
                                :required="isEffectivelyRequired('referral_id')"
                            />
                        </div>
                    </div>
                </template>
                <template v-else-if="isInsu">
                    <div class="section-row">
                        <div v-if="!isHidden('referral_id')" class="input-box w-[100%]">
                            <combobox
                                inputId="referral_id"
                                :options="referralList"
                                v-model="data.referral_id"
                                :errmsg="validationMessage.referral_id"
                                label="소개처"
                                :readonly="isFieldReadonly('referral_id')"
                                :required="isEffectivelyRequired('referral_id')"
                                :placeholder="'소개처를 선택하세요.'"
                            />
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="section-row">
                        <div v-if="!isHidden('deposit_payment_amount')" class="input-box w-[33%]">
                            <inputbox
                                inputId="deposit_payment_amount"
                                v-model="data.deposit_payment_amount"
                                :number="'n'"
                                :unit="'원'"
                                :errmsg="validationMessage.deposit_payment_amount"
                                label="보증금"
                                :placeholder="'보증금을 입력하세요.'"
                                :maxlength="10"
                                :readonly="isFieldReadonly('deposit_payment_amount')"
                                :required="isEffectivelyRequired('deposit_payment_amount')"
                            />
                        </div>
                        <div v-if="!isHidden('advance_payment')" class="input-box w-[33%]">
                            <inputbox
                                inputId="advance_payment"
                                v-model="data.advance_payment"
                                :number="'n'"
                                :unit="'원'"
                                :errmsg="validationMessage.advance_payment"
                                label="선수금"
                                :placeholder="'선수금을 입력하세요.'"
                                :maxlength="10"
                                :readonly="isFieldReadonly('advance_payment')"
                                :required="isEffectivelyRequired('advance_payment')"
                            />
                        </div>
                        <div v-if="!isHidden('payment_first_date')" class="input-box w-[33%]">
                            <DatePickerWithRegistComponent2
                                inputId="payment_first_date"
                                v-model="data.payment_first_date"
                                :errmsg="validationMessage.payment_first_date"
                                label="일 최초납부일"
                                :readonly="isFieldReadonly('payment_first_date')"
                                :required="isEffectivelyRequired('payment_first_date')"
                            />
                        </div>
                    </div>
                    <div class="section-row">
                        <div v-if="!isHidden('month_amount')" class="input-box w-[33%]">
                            <inputbox
                                inputId="month_amount"
                                v-model="data.month_amount"
                                :number="'n'"
                                :unit="'원'"
                                :errmsg="validationMessage.month_amount"
                                label="월 납부금"
                                :placeholder="'월 납부금을 입력하세요.'"
                                :maxlength="10"
                                :readonly="isFieldReadonly('month_amount')"
                                :required="isEffectivelyRequired('month_amount')"
                            />
                        </div>
                        <div v-if="!isHidden('contract_month')" class="input-box w-[22.5%]">
                            <combobox
                                inputId="contract_month"
                                :options="generateArray(120, '개월')"
                                v-model="data.contract_month"
                                :errmsg="validationMessage.contract_month"
                                label="계약개월"
                                :readonly="isFieldReadonly('contract_month')"
                                :required="isEffectivelyRequired('contract_month')"
                                :placeholder="'계약개월을 선택하세요.'"
                            />
                        </div>
                        <div v-if="!isTotalHidden" class="input-box w-[10%] flex items-center">
                            <button
                                v-if="hasExternalTotalAmount"
                                type="button"
                                @click="handleClickSchedule"
                                class="h-[2.5rem] mt-6 text-[0.875rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold w-full select-none"
                            >
                                납부스케줄
                            </button>
                            <button
                                v-else
                                type="button"
                                @click="handleSchedulePreview"
                                class="h-[2.5rem] mt-6 text-[0.875rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold w-full select-none !leading-[1rem]"
                            >
                                납부스케줄<br />미리보기
                            </button>
                        </div>
                        <div v-if="!isHidden('payment_day')" class="input-box w-[33%]">
                            <combobox
                                inputId="payment_day"
                                :options="generateArray(31, '일')"
                                v-model="data.payment_day"
                                :errmsg="validationMessage.payment_day"
                                label="납부일"
                                :readonly="isFieldReadonly('payment_day')"
                                :required="isEffectivelyRequired('payment_day')"
                                :placeholder="'납부일을 선택하세요.'"
                            />
                        </div>
                    </div>
                    <div class="section-row">
                        <div v-if="!isTotalHidden" class="input-box w-[33%]">
                            <inputbox
                                inputId="total_amount"
                                v-model="displayTotalAmount"
                                :number="'n'"
                                :unit="'원'"
                                :errmsg="validationMessage.total_amount"
                                label="총 요금"
                                :placeholder="'총 요금'"
                                :maxlength="15"
                                :readonly="true"
                                :required="isEffectivelyRequired('total_amount')"
                            />
                        </div>
                        <div v-if="!isHidden('referral_id')" :class="['input-box', isTotalHidden ? 'w-full' : 'w-[33%]']">
                            <combobox
                                inputId="referral_id"
                                :options="referralList"
                                v-model="data.referral_id"
                                :errmsg="validationMessage.referral_id"
                                label="소개처"
                                :readonly="isFieldReadonly('referral_id')"
                                :required="isEffectivelyRequired('referral_id')"
                                :placeholder="'소개처를 선택하세요.'"
                            />
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
    <Teleport to="body">
        <div v-if="showSchedulePreviewPopup" class="schedule-popup-overlay" @click.self="closeSchedulePopup">
            <div class="schedule-popup-content">
                <div v-if="scheduleList.length > 0" class="schedule-container">
                    <table class="schedule-grid select-none">
                        <thead>
                            <tr>
                                <th>총 요금</th>
                                <th>선수금</th>
                                <th>납부총액</th>
                                <th>납부횟수</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ formatCurrency(displayTotalAmount) }} 원</td>
                                <td>{{ formatCurrency(data.advance_payment) }} 원</td>
                                <td>{{ formatCurrency(paymentTotal) }} 원</td>
                                <td>{{ data.contract_month }} 회</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="schedule-container">
                    <table class="schedule-grid select-none">
                        <thead>
                            <tr>
                                <th>회차</th>
                                <th>납부예정일</th>
                                <th>납부예정금액</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="scheduleList.length === 0">
                                <td colspan="3">스케줄 정보를 계산할 수 없습니다.</td>
                            </tr>
                            <tr v-for="item in scheduleList" :key="item.installment">
                                <td>{{ item.installment }}</td>
                                <td>{{ item.paymentDate }}</td>
                                <td>{{ numberFormatter(item.paymentAmount) }} 원</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <button @click="closeSchedulePopup" class="close-button w-full">닫기</button>
            </div>
        </div>
    </Teleport>
    <!-- 납부스케줄 팝업 -->
    <Teleport to="body">
        <div v-if="showSchedulePopup" class="popup-overlay">
            <div class="popup-content !w-[65rem]">
                <div class="popup-header">
                    <h3 class="popup-title">납부스케줄</h3>
                    <button @click="closeSchedulePopup" class="close-button">&times;</button>
                </div>

                <div class="popup-body">
                    <gridlist :dataList="paymentScheduleList" />
                </div>
            </div>
        </div>
    </Teleport>
    <!-- //납부스케줄 팝업 -->
</template>
<style src="@/assets/css/form-common.css"></style>

<style scoped>
.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    transition: color 0.3s ease;
    border-radius: 0.4rem;
    background-color: #f0f0ff;
    color: #3868ff;
    align-items: center;
    /* position: absolute; */
    display: flex;
    justify-content: center;
    border: 1px solid #3868ff;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.schedule-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1002;
}

.schedule-popup-content {
    background: white;
    padding: 2rem;
    border-radius: 0.5rem;
    width: 100%;
    max-width: 35rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.popup-title {
    font-family: 'SCoreDream600';
    font-size: 1.2rem;
    color: #333;
    text-align: center;
    margin: 0;
}

.summary-section {
    border: 1px solid #e0e0e0;
    border-radius: 0.4rem;
    padding: 0.5rem;
    background-color: #f9f9f9;
}

.summary-table {
    width: 100%;
    border-collapse: collapse;
}

.summary-table th,
.summary-table td {
    padding-left: 1rem;
    padding-right: 1rem;
    font-size: 0.95rem;
}

.summary-table th {
    text-align: left;
    font-family: 'SCoreDream500';
    color: #555;
    width: 120px;
}

.summary-table td {
    text-align: right;
    font-family: 'SCoreDream400';
    color: #111;
}

.schedule-container {
    max-height: 40vh;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 0.4rem;
}

.schedule-grid {
    width: 100%;
    border-collapse: collapse;
    text-align: center;
}

.schedule-grid th,
.schedule-grid td {
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid #eee;
    font-size: 0.9rem;
}

.schedule-grid thead {
    position: sticky;
    top: 0;
    background-color: #eee;
}

.schedule-grid th {
    font-family: 'SCoreDream400';
}

.schedule-grid tbody tr:last-child td {
    border-bottom: none;
}
.schedule-grid tbody tr:nth-child(even) {
    background-color: #fafafa;
}

.schedule-container .close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.schedule-container .close-button:hover {
    background: #ddd;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.popup-content .close-button {
    background: none;
    border: none;
    font-size: 24px;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s;
}
.popup-content .close-button:hover {
    background: #ddd;
}

.popup-overlay {
    user-select: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    /* padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem; */
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 50rem;
    max-height: 80vh;
    text-align: left;
    min-width: 30rem;
    display: flex;
    flex-direction: column;
}

.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    position: relative;
}

.popup-header::after {
    content: '';
    display: block;
    width: 100%;
    height: 0.5px;
    bottom: -1rem;
    background-color: #dcdcdc;
    position: absolute;
}

.popup-body {
    margin-top: 2rem;
}

.popup-title {
    font-size: 1.2rem;
    font-family: 'SCoreDream500';
    color: #374151;
}

.popup-save-button {
    border: 1px solid #1d4ed8;
    background-color: #eff6ff;
    font-size: 0.9rem;
    color: #1d4ed8;
    font-weight: 700;
    height: 2.5rem;
    width: 100%;
    border-radius: 0.4rem;
    margin-top: 2rem;
}

.popup-save-button:hover {
    background-color: #dbeafe;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-duration: 150ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
